import { create } from 'zustand'
import { subscribeWithSelector, persist, createJSONStorage } from 'zustand/middleware'
import AsyncStorage from '@react-native-async-storage/async-storage'
import type { userInformationInterface } from '@/src/common/interfaces/user'
import parsePhoneNumber from 'libphonenumber-js'
import { useTokenStore } from '@/src/common/store/useTokenStore'
import { isEqual } from 'lodash'
import * as Sentry from '@sentry/react-native'

interface UserStoreState {
  countryCode: string
  phoneNumber: string | undefined
  parsedPhoneNumber: string | undefined
  email: string | undefined
  userInformation?: userInformationInterface
  setParsedPhoneNumber: (phoneNumber: string) => void
  setEmail: (email: string) => void
  setUserInformation: (userInformation: userInformationInterface | undefined) => void
}

/** @deprecated just use apollo with `GET_USER_INFO` */
export const useUserStore = create(
  persist(
    subscribeWithSelector<UserStoreState>((set, get) => ({
      countryCode: '+1',
      phoneNumber: undefined,
      parsedPhoneNumber: undefined,
      email: undefined,
      userInformation: undefined,
      setParsedPhoneNumber: (parsedPhoneNumber: string) => {
        if (parsedPhoneNumber) {
          set({
            parsedPhoneNumber,
            phoneNumber: parsePhoneNumber(`${get().countryCode}${parsedPhoneNumber}`)
              ?.nationalNumber, // number in plain format without country code
          })
        }
      },
      setEmail: (email: string) => set({ email }),
      setUserInformation: (userInformation?: userInformationInterface) => {
        // This looks hacky but... here's my thought process:
        // 1. This store is deprecated.
        // 2. Even though it's deprecated, we use it a lot.
        // 3. A lot of places use `useGetUserInformation` instead.
        // 4. There are a _lot_ of `useGetUserInformation` hooks active if the app has been running for a while.
        // 5. _Each hook_ is going to call `setUserInformation` with the same value.
        // 6. This is going to cause a _lot_ of re-renders.
        // 7. We don't need to re-render if the value is the same.
        // Is doing a deep comparison the bonafide best solution? Probably not.
        // If you have a better solution, please don't hesitate to undo my change and replace it with something better!
        // But right now our priority is on the dual issuance stack and this is just to address performance issues as of late.
        // I've verified via `console.time` inside of `useGetUserInformation` that this is a significant performance improvement.
        // (After a few iterations of background -> active, from 300ms each hook execution to 0.1ms)
        if (!isEqual(get().userInformation, userInformation)) {
          Sentry.setUser({ id: userInformation?.id, email: userInformation?.email })
          set({ userInformation })
        }

        if (!userInformation) {
          set({
            phoneNumber: undefined,
            parsedPhoneNumber: undefined,
            email: undefined,
            userInformation: undefined,
          })
        }
      },
    })),
    { name: 'user-store', storage: createJSONStorage(() => AsyncStorage) },
  ),
)

useTokenStore.subscribe(
  (state) => state.tokens?.accessToken,
  function handleUserInformation(accessToken) {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const { setUserInformation } = useUserStore.getState()
    if (accessToken === undefined) {
      setUserInformation(undefined)
    }
  },
)
